import React from 'react';
import AgreementPageLayout from '../components/AgreementPageLayout';

const PrivacyPolicyPage: React.FC = () => {
  return (
    <AgreementPageLayout
      title="AACore Privacy Policy"
      effectiveDate="01/01/2026"
      lastUpdated="06/10/2026"
      toc={[
        { id: 'operator-contact', title: 'Operator & Contact' },
        { id: 'summary-key-points', title: '1) Summary of Key Points' },
        { id: 'scope', title: '2) Scope' },
        { id: 'definitions', title: '3) Definitions' },
        { id: 'information-collected', title: '4) Information We Collect' },
        { id: 'how-we-use', title: '5) How We Use Information' },
        { id: 'disclosure', title: '6) How We Disclose Information' },
        { id: 'payments-stripe', title: '7) Payments, Stripe, and Financial Information' },
        { id: 'cookies-tracking', title: '8) Cookies and Tracking' },
        { id: 'choices-controls', title: '9) Your Choices and Controls' },
        { id: 'data-retention', title: '10) Data Retention' },
        { id: 'security', title: '11) Security' },
        { id: 'children-privacy', title: '12) Children\'s Privacy' },
        { id: 'businesses-third-parties', title: '13) Businesses and Third-Party Services' },
        { id: 'state-rights', title: '14) State Privacy Rights' },
        { id: 'international-users', title: '15) International Users' },
        { id: 'trial-period', title: '16) Enhancement and Trial Period' },
        { id: 'changes-policy', title: '17) Changes to This Policy' },
        { id: 'contact-us', title: '18) Contact Us' },
        { id: 'privacy-acknowledgment', title: 'Privacy Acknowledgment' },
      ]}
    >
      <p>
        This Privacy Policy explains how A&amp;A Inomatic, LLC collects, uses, discloses, retains, and protects personal
        information when you use AACore, including AACore Booking and related AACore applications, websites, and services.
      </p>

      <div className="mt-6 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
        <div className="font-semibold mb-1">Key Privacy Notice</div>
        A&amp;A Inomatic, LLC does not sell personal information and does not share personal information with third parties
        for their own marketing or advertising. AACore uses service providers, including hosting, communication, analytics,
        security, and payment providers, only to operate, support, protect, and improve the Platform. When online payments
        are enabled, payment details are handled by third-party payment processors such as Stripe; AAI is not a bank, not a
        payment processor, and does not store full card numbers, CVV codes, or full banking credentials.
      </div>

      <div id="operator-contact" className="mt-6 rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-700">
        <div className="font-semibold text-gray-900 mb-1">Operator &amp; Contact</div>
        <strong>Platform:</strong> AACore, including AACore Booking and related AACore applications, websites, and services
        (the &ldquo;Platform&rdquo;)<br />
        <strong>Operator:</strong> A&amp;A Inomatic, LLC (&ldquo;AAI,&rdquo; &ldquo;Company,&rdquo; &ldquo;we,&rdquo;
        &ldquo;us,&rdquo; &ldquo;our&rdquo;)<br />
        <strong>Contact:</strong> <a href="mailto:support@aainomatic.com">support@aainomatic.com</a><br />
        <strong>Mailing Address:</strong> Clifton, VA 20124
        <p className="mt-3 mb-0">
          This AACore Privacy Policy explains how AAI collects, uses, discloses, retains, and protects personal information
          in connection with the Platform.
        </p>
      </div>

      <h3 id="summary-key-points">1) Summary of Key Points</h3>
      <ul>
        <li>We do not sell your personal information.</li>
        <li>We do not share your personal information with third parties for their own marketing or advertising.</li>
        <li>
          If AAI sends marketing, it is sent by AAI only, using information within our Platform and systems, and you may opt
          out where required or offered.
        </li>
        <li>
          We use service providers, such as hosting, email/SMS delivery, analytics, security, customer support, and payment
          processors, only to operate, support, secure, and improve the Platform.
        </li>
        <li>Service providers are not allowed to sell your data or use it for their own marketing.</li>
        <li>
          When payments are enabled, payment processing is handled by third-party payment processors such as Stripe. AAI does
          not store full card numbers, CVV codes, or full banking credentials.
        </li>
        <li>Businesses that you choose to book with are independent third parties and may have their own privacy practices.</li>
      </ul>

      <h3 id="scope">2) Scope</h3>
      <p>This Privacy Policy applies when you use AACore, including when you:</p>
      <ul>
        <li>create, access, or manage an account;</li>
        <li>
          book, request, cancel, reschedule, or manage appointments with businesses using the Platform
          (&ldquo;Businesses&rdquo;);
        </li>
        <li>communicate with Businesses or AAI through the Platform;</li>
        <li>visit AACore websites, mobile experiences, booking pages, or related applications;</li>
        <li>
          make or receive payment-related confirmations, receipts, deposits, invoices, refunds, or related payment-status
          notices through the Platform, where enabled; or
        </li>
        <li>submit reviews, ratings, photos, messages, support requests, or other content, where enabled.</li>
      </ul>
      <p>
        This Policy applies primarily to customers, visitors, and end users. Businesses using AACore may also be subject to
        separate business agreements, payment processor terms, and other requirements.
      </p>

      <h3 id="definitions">3) Definitions</h3>
      <ul>
        <li>
          <strong>&ldquo;Personal Information&rdquo;</strong> means information that identifies, relates to, describes, or
          could reasonably be linked to an individual, such as name, phone number, email address, account identifiers,
          appointment details, messages, device information, or payment-related metadata.
        </li>
        <li>
          <strong>&ldquo;Business&rdquo;</strong> means a third-party business, provider, professional, office, store,
          organization, or individual provider that offers services through the Platform.
        </li>
        <li>
          <strong>&ldquo;Customer&rdquo;</strong> means a person who visits the Platform, creates an account, books or
          requests services, communicates through the Platform, or otherwise uses AACore as an end user.
        </li>
        <li>
          <strong>&ldquo;Service Providers&rdquo;</strong> means vendors and contractors AAI uses to operate, host, secure,
          support, analyze, communicate through, or improve the Platform, including payment processors when payment features
          are enabled.
        </li>
        <li>
          <strong>&ldquo;Payment Processor&rdquo;</strong> means Stripe or another third-party payment provider used for
          payment processing, deposits, refunds, payouts, disputes, chargebacks, identity verification, or related payment
          services.
        </li>
        <li>
          <strong>&ldquo;Targeted Marketing,&rdquo; as used in this Policy,</strong> means marketing messages from AAI
          tailored based on Platform activity, such as reminders, promotions, suggested services, or Platform updates. It does
          not mean selling data, third-party advertising, or allowing third parties to market to you using your data.
        </li>
      </ul>

      <h3 id="information-collected">4) Information We Collect</h3>
      <p>
        We collect information in three main ways: information you provide, information generated through your use of the
        Platform, and information provided by Businesses or service providers in connection with your bookings,
        communications, or transactions.
      </p>

      <h4>4.1 Information you provide</h4>
      <ul>
        <li>
          <strong>Account information:</strong> name, email address, phone number, login credentials, verification codes,
          notification preferences, and account settings.
        </li>
        <li>
          <strong>Booking information:</strong> appointment details, selected services, requested staff or provider,
          preferences, service notes, cancellation or reschedule activity, and booking history.
        </li>
        <li>
          <strong>Communications:</strong> messages to or from Businesses, support requests, customer feedback, email/SMS
          interactions, and related communication records.
        </li>
        <li>
          <strong>User content:</strong> reviews, ratings, photos, comments, text, images, and other content you submit,
          where enabled.
        </li>
        <li>
          <strong>Support information:</strong> screenshots, troubleshooting details, issue descriptions, device details, and
          other information you provide when contacting support.
        </li>
      </ul>

      <h4>4.2 Information collected automatically</h4>
      <ul>
        <li>
          <strong>Device and usage data:</strong> IP address, browser type, device identifiers, operating system, app or
          browser version, pages/screens visited, actions taken, timestamps, referring pages, and diagnostic logs.
        </li>
        <li>
          <strong>Cookies and similar technologies:</strong> web cookies, local storage, session identifiers, and similar
          technologies used to keep you signed in, remember preferences, improve performance, prevent fraud, and understand
          usage.
        </li>
        <li>
          <strong>Approximate location:</strong> city/state or general region derived from IP address, used for security,
          fraud prevention, analytics, and to show relevant Businesses where applicable.
        </li>
      </ul>

      <h4>4.3 Payments, deposits, and payment-related data</h4>
      <p>
        If you pay through the Platform, payment processing is typically handled by a Payment Processor such as Stripe. AAI
        may receive limited payment-related metadata such as payment status, transaction identifiers, payment intent
        identifiers, invoice identifiers, refund identifiers, receipt information, amount, currency, timestamps, customer
        identifiers, connected account identifiers, last four digits of a card where provided by the processor, card brand,
        billing zip code where provided, and audit records.
      </p>
      <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-700">
        <p className="mb-0">
          <strong>No full card or banking storage by AAI.</strong> AAI does not store full payment card numbers, CVV codes,
          full bank account numbers, full routing numbers, or full payout banking credentials. Full payment card and banking
          information should be entered only into Payment Processor-controlled flows or hosted payment interfaces.
        </p>
      </div>

      <h4>4.4 Information from Businesses</h4>
      <p>
        Businesses may provide, update, or generate booking-related information, such as confirmation status, reschedule
        requests, cancellation notes, service completion, appointment notes, customer preferences, invoices, refund status, or
        customer communication records. Businesses control what they enter into their Business systems and may have their own
        privacy practices.
      </p>

      <h4>4.5 Sensitive information</h4>
      <p>
        You should not submit sensitive information unless it is necessary for the service you requested and you are
        comfortable sharing it with the selected Business. Unless AAI expressly agrees in a signed written agreement, the
        Platform is not intended for Social Security numbers, full payment credentials, government identity documents, biometric
        identifiers, detailed health records, HIPAA protected health information, or other highly regulated sensitive
        information.
      </p>

      <h3 id="how-we-use">5) How We Use Information</h3>
      <p>
        We use personal information to provide, operate, support, secure, and improve the Platform, including to:
      </p>

      <h4>5.1 Provide and operate the Platform</h4>
      <ul>
        <li>create and manage accounts;</li>
        <li>verify email addresses or phone numbers using one-time codes or other methods;</li>
        <li>
          enable bookings, confirmations, cancellations, reschedules, reminders, waitlists, invoices, receipts, and account
          notices;
        </li>
        <li>display booking history, service selections, preferences, and Business information;</li>
        <li>route communications between you, Businesses, AAI, and Service Providers as needed; and</li>
        <li>provide customer support and troubleshooting.</li>
      </ul>

      <h4>5.2 Improve and secure the Platform</h4>
      <ul>
        <li>monitor performance, troubleshoot errors, test features, fix bugs, and improve usability;</li>
        <li>prevent fraud, abuse, spam, unauthorized access, payment disputes, and security incidents;</li>
        <li>maintain logs, audit records, and security records for compliance and operational needs; and</li>
        <li>analyze aggregated or de-identified usage trends to improve AACore.</li>
      </ul>

      <h4>5.3 Communicate with you</h4>
      <ul>
        <li>
          send transactional messages, including verification codes, booking confirmations, reminders, receipts, security
          alerts, policy notices, and support responses;
        </li>
        <li>notify you about account, booking, payment, privacy, security, or Platform updates; and</li>
        <li>respond to support requests, feedback, and inquiries.</li>
      </ul>

      <h4>5.4 Marketing by AAI only</h4>
      <p>
        Where permitted, AAI may send Platform promotions, service suggestions, Business suggestions, reminders, or updates
        from AAI. AAI does not sell or share personal information for third-party marketing, and we do not allow third parties
        to market to you using your personal information.
      </p>

      <h3 id="disclosure">6) How We Disclose Information</h3>
      <p>
        We do not sell your personal information. We do not share your personal information with third parties for their own
        marketing or advertising. We may disclose personal information only as described in this Policy.
      </p>

      <h4>6.1 To Businesses you choose</h4>
      <p>
        When you book, request, communicate with, purchase from, or otherwise interact with a Business through the Platform,
        we may share information needed for that Business to provide and manage the service, such as your name, phone number,
        email address, appointment details, service selections, notes or preferences you submit, payment status, cancellation
        or reschedule details, and communications related to the booking.
      </p>
      <p>
        Businesses are independent third parties. Once information is provided to a Business you choose, that Business may
        process the information under its own privacy practices, legal obligations, and customer policies. AAI is not
        responsible for a Business&apos;s independent privacy practices, service operations, licensing, compliance, or
        customer handling.
      </p>

      <h4>6.2 To Service Providers</h4>
      <p>
        We use trusted Service Providers to help operate AACore, such as hosting providers, database providers, email/SMS
        providers, analytics tools, security tools, support tools, and Payment Processors. They may process personal
        information only to provide services to AAI or as otherwise permitted by law and contract.
      </p>
      <p>
        Service Providers are required to protect information, use it only to provide services to AAI, not sell it, and not
        use it for their own marketing.
      </p>

      <h4>6.3 To Payment Processors</h4>
      <p>
        If payment features are enabled, payment information may be processed directly by Payment Processors such as Stripe.
        Payment Processors may use information according to their own terms, privacy notices, fraud prevention rules, identity
        verification requirements, card network rules, and legal obligations. AAI does not control Payment Processor
        underwriting, risk reviews, holds, reserves, payout timing, chargebacks, or dispute outcomes.
      </p>

      <h4>6.4 For legal, safety, security, and compliance</h4>
      <p>
        We may disclose information when we believe it is reasonably necessary to comply with law, regulation, subpoena, court
        order, government request, processor requirement, card network rule, or legal process; to protect the rights, safety,
        and security of users, Businesses, AAI, Service Providers, or Payment Processors; to investigate fraud, abuse,
        security incidents, disputes, or policy violations; or to enforce agreements and Platform rules.
      </p>

      <h4>6.5 Business transfers</h4>
      <p>
        If AAI is involved in a merger, acquisition, financing, reorganization, sale of assets, corporate transaction,
        bankruptcy, or similar event, information may be transferred or disclosed as part of that transaction, subject to
        this Policy or any successor policy as permitted by law.
      </p>

      <h3 id="payments-stripe">7) Payments, Stripe, and Financial Information</h3>
      <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-700">
        <p className="mb-0">
          <strong>No Stripe affiliation and no banking custody.</strong> AAI is not owned by, operated by, sponsored by,
          endorsed by, affiliated with, or acting as an agent of Stripe. AAI is not a bank, payment processor, money
          transmitter, escrow agent, merchant of record, financial institution, credit provider, payment facilitator, payment
          intermediary, or fiduciary for funds. AAI does not hold, control, receive custody of, settle, or guarantee Customer
          funds, Business payouts, deposits, refunds, reversals, or chargeback outcomes.
        </p>
      </div>
      <p>
        If payment features are enabled, payment processing is provided by a Payment Processor. The Payment Processor may
        collect, process, store, and use payment and identity information under its own terms and privacy practices. AAI may
        receive limited payment metadata needed to show payment status, reconcile transactions, provide receipts, support
        disputes, prevent fraud, and operate the Platform.
      </p>
      <p>
        You should never send full card numbers, CVV codes, full bank account numbers, or full routing numbers to AAI by email,
        support message, chat, text message, booking note, review, photo, or any non-payment field.
      </p>

      <h3 id="cookies-tracking">8) Cookies and Tracking</h3>
      <p>
        We use cookies and similar technologies to keep you signed in, remember preferences, analyze usage, improve
        performance, detect and prevent fraud, support security, and understand how the Platform is used.
      </p>
      <p>
        You can control cookies through your browser settings. Some Platform features may not work correctly without essential
        cookies or local storage.
      </p>
      <p>
        We do not use cookies to sell personal information or allow third parties to use your personal information for their
        own marketing.
      </p>

      <h3 id="choices-controls">9) Your Choices and Controls</h3>

      <h4>9.1 Account information</h4>
      <p>
        You may review and update certain account information within the Platform where available. You are responsible for
        keeping your name, phone number, email address, and booking details accurate.
      </p>

      <h4>9.2 Marketing preferences</h4>
      <p>
        You may opt out of marketing messages by using an unsubscribe link in emails where provided, replying &ldquo;STOP&rdquo;
        to SMS marketing messages where supported, changing available account settings, or contacting{' '}
        <a href="mailto:support@aainomatic.com">support@aainomatic.com</a>.
      </p>
      <p>
        Transactional messages, such as booking confirmations, reminders, security alerts, account notices, receipts, and
        support messages, may still be sent as needed to operate the Platform and your bookings.
      </p>

      <h4>9.3 SMS/text messages</h4>
      <p>
        Message frequency varies. Message and data rates may apply. You may opt out of supported SMS messaging by replying
        &ldquo;STOP.&rdquo; For help, reply &ldquo;HELP&rdquo; where supported or contact{' '}
        <a href="mailto:support@aainomatic.com">support@aainomatic.com</a>.
      </p>

      <h4>9.4 Reviews and photos</h4>
      <p>
        If you post reviews, ratings, photos, or other user content, you may be able to edit or delete them depending on
        Platform features and moderation policies. AAI may retain records where needed for legal, security, audit, dispute,
        fraud-prevention, or operational purposes.
      </p>

      <h3 id="data-retention">10) Data Retention</h3>
      <p>
        We keep personal information for as long as reasonably needed to provide the Platform, maintain your account, support
        bookings and communications, comply with legal, tax, accounting, payment, security, and compliance requirements,
        resolve disputes, prevent fraud or abuse, enforce agreements, and maintain business records.
      </p>
      <p>
        Retention periods may vary depending on the type of information, account status, Business relationship, legal
        requirements, payment records, dispute status, security needs, backups, and operational requirements. Backups and
        logs may retain information for a limited period after deletion from active systems.
      </p>

      <h3 id="security">11) Security</h3>
      <p>
        We use reasonable administrative, technical, and physical safeguards designed to protect personal information,
        including access controls, encryption in transit where appropriate, authentication controls, logging, and vendor
        safeguards.
      </p>
      <p>
        No method of transmission, processing, or storage is 100% secure. To the maximum extent permitted by law, AAI is not
        responsible for unauthorized access, data loss, account compromise, or privacy incidents caused by user error, weak
        passwords, shared credentials, malware, phishing, insecure devices, unauthorized staff access, Business-side misuse,
        third-party provider failures, or events outside AAI&apos;s reasonable control.
      </p>
      <p>
        You are responsible for using strong credentials, protecting verification codes, securing your devices and accounts,
        and promptly notifying AAI if you suspect unauthorized access.
      </p>

      <h3 id="children-privacy">12) Children&apos;s Privacy</h3>
      <p>
        The Platform is not intended for children under 13, and in some jurisdictions may not be intended for children under
        16. We do not knowingly collect personal information from children without appropriate consent.
      </p>
      <p>
        If you book services for a minor or dependent, you represent that you have legal authority to do so and to share
        necessary information. If you believe a child has provided information without proper consent, contact{' '}
        <a href="mailto:support@aainomatic.com">support@aainomatic.com</a> to request review or deletion where required.
      </p>

      <h3 id="businesses-third-parties">13) Businesses and Third-Party Services</h3>
      <p>
        Businesses are independent third parties and may have their own websites, forms, intake processes, privacy notices,
        payment practices, refund policies, recordkeeping practices, and legal obligations. AAI is not responsible for Business
        privacy practices or how Businesses use information after you choose to share it with them through the Platform or
        directly.
      </p>
      <p>
        The Platform may link to or depend on third-party services, including Payment Processors, SMS providers, email
        providers, maps, browsers, operating systems, app stores, analytics tools, support tools, and security tools.
        Third-party services are governed by their own terms and privacy practices. AAI is not responsible for third-party
        privacy practices, service outages, security incidents, payment decisions, or data handling outside AAI&apos;s control.
      </p>

      <h3 id="state-rights">14) State Privacy Rights</h3>
      <p>
        Depending on where you live, you may have rights to access the personal information we hold about you, correct
        inaccurate information, delete certain information, obtain a copy of your information, opt out of certain processing
        where applicable, or appeal a privacy request decision where required by law.
      </p>
      <p>We do not sell personal information and we do not share personal information for cross-context behavioral advertising.</p>
      <p>
        To submit a request, contact <a href="mailto:support@aainomatic.com">support@aainomatic.com</a>. We may need to
        verify your identity and account relationship before completing a request. We may deny or limit requests where
        permitted by law, including when information must be retained for security, fraud prevention, legal compliance,
        payment records, dispute resolution, accounting, or operational needs.
      </p>

      <h3 id="international-users">15) International Users</h3>
      <p>
        AAI operates in the United States. If you access the Platform from outside the United States, you understand that your
        information may be transferred to, stored in, and processed in the United States, where privacy laws may differ from
        those in your location.
      </p>

      <h3 id="trial-period">16) Enhancement and Trial Period</h3>
      <p>
        AACore Booking is currently being provided during an enhancement, testing, and trial period through December 31,
        2026, unless AAI announces a different date or paid plan. During this period, features, workflows, settings, data
        fields, integrations, payment options, communication features, reports, and retention practices may change as the
        Platform is improved.
      </p>
      <p>
        This Privacy Policy does not create any warranty, uptime commitment, support commitment, data archive obligation, or
        guarantee that all trial-period features will remain available. Users and Businesses should maintain appropriate backup
        records and contingency procedures.
      </p>

      <h3 id="changes-policy">17) Changes to This Policy</h3>
      <p>
        We may update this Privacy Policy from time to time. We will update the &ldquo;Last updated&rdquo; date and post the
        revised policy. Continued use of the Platform after changes become effective means you accept the updated policy to
        the extent permitted by law.
      </p>

      <h3 id="contact-us">18) Contact Us</h3>
      <p>Questions, notices, privacy requests, or security concerns may be sent to:</p>
      <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-700">
        <p className="mb-0">
          <strong>A&amp;A Inomatic, LLC</strong>
          <br />
          Clifton, VA 20124
          <br />
          <a href="mailto:support@aainomatic.com">support@aainomatic.com</a>
        </p>
      </div>

      <div id="privacy-acknowledgment" className="rounded-lg border border-gray-200 bg-blue-50 px-4 py-3 text-sm text-gray-700 mt-8">
        <h3 className="mt-0">Privacy Acknowledgment</h3>
        <p className="mb-0">
          By using the Platform, you acknowledge that you have read this AACore Privacy Policy and understand that AAI may
          collect, use, disclose, retain, and protect personal information as described above. You also acknowledge that
          Businesses and Payment Processors are independent third parties and may process information under their own terms
          and privacy practices.
        </p>
      </div>
    </AgreementPageLayout>
  );
};

export default PrivacyPolicyPage;
